import { $connection, $drafts, $hotbarIndex, $lookAt, $myAssets, $placeMode, $plots, $presence, $remoteCount, $session, $worldConfig } from "../state/stores";
import { $roomPhase } from "../room/phase";
import { $compass } from "../state/compass";
import { $lookMarker } from "../state/markers";
import { $slotNames, resolveSlotNames, slotView } from "../state/hotbar";
import { $hotbar, $unavailable } from "../state/settings";
import { compassMarkers } from "../world/compass";
import type { WorldConfig } from "../world/config";
import { lookPrompt } from "./lookPrompt";
import { bannerText } from "../world/plots";
import { mountCompass } from "./compass";
import { mountMailBadge } from "./mail";
import { $touchMode, hint } from "../state/touch";

/** What the desktop asks after a panel closes, to take the pointer lock again; touch has no lock. */
export const CLICK_TO_RESUME = "點擊畫面以繼續";

export interface HUD {
  setCoords(x: number, y: number, z: number, zone: { x: number; z: number }, plotLine: string): void;
  setPrompt(text: string | null): void;
  setHeading(yawDeg: number, x: number, z: number): void;
}

export function mountHUD(root: HTMLElement): HUD {
  // insert rather than replace: the frame rate readout is already in #hud when the world mounts
  root.insertAdjacentHTML("afterbegin", `
    <div class="crosshair"></div>
    <div class="hud-top-left"><span id="hud-coords"></span><span id="hud-conn"></span><div id="hud-plot"></div></div>
    <div class="hud-top-right"><span id="hud-players"></span></div>
    <div class="hud-banner" id="hud-banner"></div>
    <div class="hud-prompt" id="hud-prompt"></div>
    <div class="compass" id="compass"></div>
    <div class="mail-badge" id="mail-badge"></div>
    <div class="hotbar" id="hotbar"></div>
  `);
  // the lobby's hotbar, compass and coordinates mean nothing in the room (style.css .in-room)
  $roomPhase.subscribe((p) => document.body.classList.toggle("in-room", p !== "lobby"));
  const coords = root.querySelector<HTMLElement>("#hud-coords")!;
  const plotRow = root.querySelector<HTMLElement>("#hud-plot")!;
  const conn = root.querySelector<HTMLElement>("#hud-conn")!;
  const players = root.querySelector<HTMLElement>("#hud-players")!;
  const prompt = root.querySelector<HTMLElement>("#hud-prompt")!;
  const banner = root.querySelector<HTMLElement>("#hud-banner")!;
  const hotbar = root.querySelector<HTMLElement>("#hotbar")!;
  const compass = mountCompass(root.querySelector<HTMLElement>("#compass")!);
  mountMailBadge(root.querySelector<HTMLElement>("#mail-badge")!);
  // estate, library and marks follow the stores, so a move or a new mark shows without a reload
  const syncMarkers = () => {
    const cfg = $worldConfig.get() as WorldConfig | null;
    compass.setMarkers(compassMarkers($plots.get(), $session.get()?.id ?? null, cfg?.landmarks ?? [], $compass.get()));
  };
  $plots.subscribe(syncMarkers);
  $session.subscribe(syncMarkers);
  $compass.subscribe(syncMarkers);
  $worldConfig.subscribe(syncMarkers);

  const slots: HTMLElement[] = [];
  for (let i = 0; i < 9; i++) {
    const s = document.createElement("div");
    s.className = "slot";
    s.innerHTML = `<span class="num">${i + 1}</span><span class="label"></span>`;
    hotbar.appendChild(s);
    slots.push(s);
  }
  const renderHotbar = () => {
    const bar = $hotbar.get(), idx = $hotbarIndex.get(), assets = $myAssets.get(), names = $slotNames.get(), gone = $unavailable.get();
    slots.forEach((s, i) => {
      const slot = bar[i] ?? null;
      const view = slotView(slot, assets, names, gone);
      s.classList.toggle("active", i === idx);
      s.classList.toggle("filled", !!slot);
      s.classList.toggle("unavailable", view.unavailable);
      s.querySelector(".label")!.textContent = view.text;
    });
    hotbar.classList.toggle("placing", !!$placeMode.get());
  };
  // names of public or shared assets are looked up once; a failed lookup greys the slot (spec 2g §3.2)
  const resolveNames = () => void resolveSlotNames($hotbar.get(), $myAssets.get());
  $hotbar.subscribe(() => { resolveNames(); renderHotbar(); });
  $myAssets.subscribe(() => { resolveNames(); renderHotbar(); });
  $hotbarIndex.subscribe(renderHotbar);
  $slotNames.subscribe(renderHotbar);
  $unavailable.subscribe(renderHotbar);
  $placeMode.subscribe(renderHotbar);
  $connection.subscribe((s) => { conn.textContent = s === "online" ? "" : s === "connecting" ? " · 連線中…" : " · 離線"; });
  $remoteCount.subscribe((n) => { players.textContent = `${n + ($session.get() ? 1 : 0)} 人在線`; });
  const renderBanner = (): void => {
    const p = $presence.get();
    banner.textContent = p ? bannerText(p, $drafts.get()) : "";
    banner.classList.toggle("show", !!p);
  };
  $presence.subscribe(renderBanner);
  $drafts.subscribe(renderBanner);
  const renderLook = () => {
    const k = $lookMarker.get();
    prompt.textContent = k ? hint("[E] 開啟標記", "點「標記」") : lookPrompt($lookAt.get());
  };
  $lookAt.subscribe(renderLook);
  $lookMarker.subscribe(renderLook);
  $touchMode.subscribe((on) => {
    renderBanner();
    if ($lookAt.get() || $lookMarker.get()) renderLook();
    // the desktop prompt set at boot, before the first touch, says nothing to a finger (spec 2p §2.1)
    else if (on && prompt.textContent === CLICK_TO_RESUME) prompt.textContent = "";
  });

  return {
    setCoords(x, y, z, zone, line) {
      coords.textContent = `${x.toFixed(1)}, ${y.toFixed(1)}, ${z.toFixed(1)}  區 ${zone.x},${zone.z}`;
      plotRow.textContent = line;
    },
    setPrompt(text) { if (!$lookAt.get() && !$lookMarker.get()) prompt.textContent = text ?? ""; },
    setHeading(yawDeg, x, z) { compass.update(yawDeg, x, z); },
  };
}
